import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { getTokens } from "../utils/auth";
import {
  getPublicSettingsApiSiteSettingsPublicGet,
  listSettingsApiSiteSettingsGet,
  getSettingApiSiteSettingsKeyGet,
  updateSettingApiSiteSettingsKeyPut,
  createSettingApiSiteSettingsPost,
} from "../api/generated";
import type {
  PublicSiteSettingsResponse,
  SiteSettingResponse,
  SiteSettingsListResponse,
  SiteSettingUpdate,
  SiteSettingCreate,
  GetPublicSettingsApiSiteSettingsPublicGetResponse,
  ListSettingsApiSiteSettingsGetResponse,
  GetSettingApiSiteSettingsKeyGetResponse,
} from "../api/generated";

// Re-export types for convenience
export type PublicSiteSettings = PublicSiteSettingsResponse;
export type SiteSetting = SiteSettingResponse;
export type { SiteSettingsListResponse, SiteSettingUpdate, SiteSettingCreate };

/**
 * Hook for fetching public site settings.
 * This endpoint is accessible without authentication.
 */
export function usePublicSiteSettings() {
  return useQuery<GetPublicSettingsApiSiteSettingsPublicGetResponse>({
    queryKey: ["site-settings", "public"],
    queryFn: async () => {
      const response = await getPublicSettingsApiSiteSettingsPublicGet();

      if (!response.data) {
        throw new Error("Failed to fetch public site settings");
      }

      return response.data;
    },
    staleTime: 0, // Always refetch when invalidated - settings can change via admin
    refetchOnMount: true, // Refetch when component mounts to get latest
  });
}

/**
 * Hook for fetching all site settings (admin only).
 */
export function useSiteSettings() {
  const { accessToken } = getTokens();

  return useQuery<ListSettingsApiSiteSettingsGetResponse>({
    queryKey: ["site-settings", "all"],
    queryFn: async () => {
      if (!accessToken) {
        throw new Error("No access token found");
      }

      const response = await listSettingsApiSiteSettingsGet();

      if (!response.data) {
        throw new Error("Failed to fetch site settings");
      }

      return response.data;
    },
    enabled: !!accessToken,
  });
}

/**
 * Hook for fetching a single site setting by key (admin only).
 */
export function useSiteSetting(key: string | null) {
  const { accessToken } = getTokens();

  return useQuery<GetSettingApiSiteSettingsKeyGetResponse>({
    queryKey: ["site-settings", key],
    queryFn: async () => {
      if (!accessToken) {
        throw new Error("No access token found");
      }
      if (!key) {
        throw new Error("Setting key is required");
      }

      const response = await getSettingApiSiteSettingsKeyGet({
        path: { key },
      });

      if (!response.data) {
        throw new Error("Failed to fetch site setting");
      }

      return response.data;
    },
    enabled: !!accessToken && !!key,
  });
}

/**
 * Hook for updating a site setting (admin only).
 */
export function useUpdateSiteSetting() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { accessToken } = getTokens();

  return useMutation({
    mutationFn: async ({
      key,
      data,
    }: {
      key: string;
      data: SiteSettingUpdate;
    }) => {
      if (!accessToken) {
        throw new Error("No access token found");
      }

      const response = await updateSettingApiSiteSettingsKeyPut({
        path: { key },
        body: data,
      });

      if (!response.data) {
        if (response.error) {
          const errorDetail = response.error.detail;
          let message = "Failed to update setting";

          if (typeof errorDetail === "string") {
            message = errorDetail;
          } else if (Array.isArray(errorDetail) && errorDetail.length > 0) {
            // Handle validation errors array
            message = errorDetail[0]?.msg || message;
          } else if (errorDetail && typeof errorDetail === "object") {
            message = (errorDetail as any)?.message || message;
          }

          throw new Error(message);
        }
        throw new Error("Failed to update setting");
      }

      return response.data;
    },
    onSuccess: (_, variables) => {
      // Invalidate and refetch both public and admin queries
      queryClient.invalidateQueries({ queryKey: ["site-settings"] });
      // Force immediate refetch of public settings so UI updates right away
      queryClient.refetchQueries({ queryKey: ["site-settings", "public"] });
      enqueueSnackbar("Hero image updated successfully", {
        variant: "success",
      });
    },
    onError: (error: any) => {
      const message = error.message || "Failed to update setting";
      enqueueSnackbar(message, { variant: "error" });
    },
  });
}

/**
 * Hook for creating a new site setting (admin only).
 */
export function useCreateSiteSetting() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { accessToken } = getTokens();

  return useMutation({
    mutationFn: async (data: SiteSettingCreate) => {
      if (!accessToken) {
        throw new Error("No access token found");
      }

      const response = await createSettingApiSiteSettingsPost({
        body: data,
      });

      if (!response.data) {
        if (response.error) {
          const errorDetail = response.error.detail;
          let message = "Failed to create setting";

          if (typeof errorDetail === "string") {
            message = errorDetail;
          } else if (Array.isArray(errorDetail) && errorDetail.length > 0) {
            // Handle validation errors array
            message = errorDetail[0]?.msg || message;
          } else if (errorDetail && typeof errorDetail === "object") {
            message = (errorDetail as any)?.message || message;
          }

          throw new Error(message);
        }
        throw new Error("Failed to create setting");
      }

      return response.data;
    },
    onSuccess: () => {
      // Invalidate and refetch both public and admin queries
      queryClient.invalidateQueries({ queryKey: ["site-settings"] });
      // Force immediate refetch of public settings so UI updates right away
      queryClient.refetchQueries({ queryKey: ["site-settings", "public"] });
      enqueueSnackbar("Setting created successfully", {
        variant: "success",
      });
    },
    onError: (error: any) => {
      const message = error.message || "Failed to create setting";
      enqueueSnackbar(message, { variant: "error" });
    },
  });
}
